import {
    formatDate,
    isNotNullOrEmptyObject,
    isNullOrEmptyArray,
    isNullOrEmptyObject,
} from "../../../shared/Utils";

// Date utility functions
export const getLastModifiedDate = (comment, file) => {
    if (isNullOrEmptyObject(comment)) {
        return file?.modified;
    } else if (isNullOrEmptyObject(file)) {
        return comment?.modified;
    } else {
        return comment?.modified > file?.modified ? comment?.modified : file?.modified;
    }
};

export const getCreatedDate = (comment, file) => {
    if (isNullOrEmptyObject(comment)) {
        return file?.created;
    } else if (isNullOrEmptyObject(file)) {
        return comment?.created;
    } else {
        return comment?.created < file?.created ? comment?.created : file?.created;
    }
};

export const getTime = date => {
    if (isNullOrEmptyObject(date)) {
        return "";
    }
    const time = date.split("T")[1];
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
};

// Pagination utility
export const calculatePagination = (currentPage, pageSize) => {
    return {
        top: pageSize,
        skip: (currentPage - 1) * pageSize,
    };
};

// Data mapping utility
export const mapApiDataToTableFormat = apiData => {
    if (isNullOrEmptyArray(apiData)) {
        return [];
    }

    // Separate records with null groupId (individual records) from grouped records
    const recordsWithNullGroupId = apiData.filter(
        item => item.groupId === null || item.groupId === undefined
    );
    const recordsWithGroupId = apiData.filter(
        item => item.groupId !== null && item.groupId !== undefined
    );

    // Process records with null groupId as individual rows
    const individualRecords = recordsWithNullGroupId.map(item => {
        const isComment = item.commentType === "COMMENT";
        const isFile = item.commentType === "FILE";
        const selectedDate = item.modified || item.created;

        return {
            // Both id and commentId are from customer_comments table
            // For individual records, they're the same ID
            id: item.id,
            commentId: item.id,
            groupId: item.groupId,
            user: item.accountName,
            date: formatDate({
                date: selectedDate,
                format: "en-US",
                includeTime: false,
                separator: "/",
            }),
            time: getTime(selectedDate),
            comment: isComment ? item.entry : "-",
            file: isFile ? item.entry : "",
            _originalIndex: apiData.indexOf(item), // Track original position
        };
    });

    // Process grouped records (with non-null groupId)
    const uniqueGroupIds = [...new Set(recordsWithGroupId.map(item => item.groupId))];

    const commentsAndFileGroups = uniqueGroupIds.map(groupId => {
        const groupData = recordsWithGroupId.filter(item => item.groupId === groupId);
        const groupComment = groupData.find(item => item.commentType === "COMMENT");
        const groupFile = groupData.find(item => item.commentType === "FILE");

        const selectedDate =
            getLastModifiedDate(groupComment, groupFile) || getCreatedDate(groupComment, groupFile);

        // Use the earliest index from the group to maintain order
        const firstItemInGroup = groupData[0];
        const originalIndex = apiData.indexOf(firstItemInGroup);

        return {
            // Both id and commentId are from customer_comments table, but point to different records:
            // - id: FILE record's ID (needed to download the file attachment)
            // - commentId: COMMENT record's ID (needed for PUT/DELETE operations on the group)
            id: groupFile?.id || groupComment?.id,
            commentId: groupComment?.id || groupFile?.id,
            groupId,
            user: groupComment?.accountName || groupFile?.accountName,
            date: formatDate({
                date: selectedDate,
                format: "en-US",
                includeTime: false,
                separator: "/",
            }),
            time: getTime(selectedDate),
            comment: isNotNullOrEmptyObject(groupComment) ? groupComment.entry : "-",
            file: isNotNullOrEmptyObject(groupFile) ? groupFile.entry : "",
            _originalIndex: originalIndex, // Track original position
        };
    });

    // Combine and sort by original index to maintain API order
    const combined = [...individualRecords, ...commentsAndFileGroups];
    combined.sort((a, b) => a._originalIndex - b._originalIndex);

    // Remove the temporary _originalIndex property
    return combined.map(item => {
        delete item._originalIndex;
        return item;
    });
};
